/**
 * ObservatoryService - Service for managing observability metrics data
 * Handles API calls related to agent observability, traces, and LLM metrics
 */

class ObservatoryService {
  
  /**
   * Base URL for the API
   */
  static baseUrl = process.env.REACT_APP_OBSERVATORY_URL || 'http://localhost:9038';

  /**
   * Get filter options (distinct agent names, types, execution contexts)
   * Filtered by the selected time period to show only relevant options
   * @param {string} timePeriod - Time period to filter by (e.g., "7d", "24h")
   * @returns {Promise<Object>} Filter options
   */
  static async getFilterOptions(timePeriod = '7d') {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('time_period', timePeriod);

      const response = await fetch(
        `${this.baseUrl}/api/observatory/filters?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load filter options');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getFilterOptions error:', error);
      throw new Error(`Failed to load filter options: ${error.message}`);
    }
  }

  /**
   * Get last computation timestamp
   * @returns {Promise<Object>} Last computation info
   */
  static async getLastComputation() {
    try {
      const response = await fetch(`${this.baseUrl}/api/observatory/last-computation`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to get last computation');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getLastComputation error:', error);
      throw new Error(`Failed to get last computation: ${error.message}`);
    }
  }

  /**
   * Trigger metrics computation for all time periods
   * @returns {Promise<Object>} Computation status
   */
  static async computeMetrics() {
    try {
      const response = await fetch(`${this.baseUrl}/api/observatory/compute-metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to compute metrics');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.computeMetrics error:', error);
      throw new Error(`Failed to compute metrics: ${error.message}`);
    }
  }

  /**
   * Get chart data for a specific chart
   * @param {string} chartId - Chart identifier (chart1, chart2, etc.)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getChartData(chartId, filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filter parameters
      if (filters.timePeriod) queryParams.append('time_period', filters.timePeriod);
      if (filters.agentNames && filters.agentNames.length > 0) {
        filters.agentNames.forEach(name => queryParams.append('agent_names', name));
      }
      if (filters.agentTypes && filters.agentTypes.length > 0) {
        filters.agentTypes.forEach(type => queryParams.append('agent_types', type));
      }
      if (filters.executionContexts && filters.executionContexts.length > 0) {
        filters.executionContexts.forEach(ctx => queryParams.append('execution_contexts', ctx));
      }

      const response = await fetch(
        `${this.baseUrl}/api/observatory/chart-data/${chartId}?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || `Failed to load chart ${chartId} data`);
      }

      return result.data;

    } catch (error) {
      console.error(`ObservatoryService.getChartData(${chartId}) error:`, error);
      throw new Error(`Failed to load chart data: ${error.message}`);
    }
  }

  /**
   * Get data for Chart #1: Tokens In/Out (Clustered Bar)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getTokensInOutData(filters = {}) {
    return this.getChartData('chart1', filters);
  }

  /**
   * Get data for Chart #2: Trace Count / LLM Call Count (Clustered Bar)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getTraceCountData(filters = {}) {
    return this.getChartData('chart2', filters);
  }

  /**
   * Get data for Chart #3: Trace/LLM Latency (Clustered Bar)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getLatencyData(filters = {}) {
    return this.getChartData('chart3', filters);
  }

  /**
   * Get data for Chart #4: Cost Bar Chart (Stacked Bar)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getCostBarData(filters = {}) {
    return this.getChartData('chart4', filters);
  }

  /**
   * Get data for Chart #5: Cost Pie Chart (Pie)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getCostPieData(filters = {}) {
    return this.getChartData('chart5', filters);
  }

  /**
   * Get data for Chart #6: Cost Per Trace (Stacked Bar)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getCostPerTraceData(filters = {}) {
    return this.getChartData('chart6', filters);
  }

  /**
   * Get data for Chart #7: Token/Trace (Stacked Bar)
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Chart data
   */
  static async getTokenPerTraceData(filters = {}) {
    return this.getChartData('chart7', filters);
  }

  /**
   * Get all chart data in parallel
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} All charts data
   */
  static async getAllChartsData(filters = {}) {
    try {
      const [
        chart1,
        chart2,
        chart3,
        chart4,
        chart5,
        chart6,
        chart7
      ] = await Promise.allSettled([
        this.getTokensInOutData(filters),
        this.getTraceCountData(filters),
        this.getLatencyData(filters),
        this.getCostBarData(filters),
        this.getCostPieData(filters),
        this.getCostPerTraceData(filters),
        this.getTokenPerTraceData(filters)
      ]);

      return {
        chart1: chart1.status === 'fulfilled' ? chart1.value : { error: chart1.reason?.message },
        chart2: chart2.status === 'fulfilled' ? chart2.value : { error: chart2.reason?.message },
        chart3: chart3.status === 'fulfilled' ? chart3.value : { error: chart3.reason?.message },
        chart4: chart4.status === 'fulfilled' ? chart4.value : { error: chart4.reason?.message },
        chart5: chart5.status === 'fulfilled' ? chart5.value : { error: chart5.reason?.message },
        chart6: chart6.status === 'fulfilled' ? chart6.value : { error: chart6.reason?.message },
        chart7: chart7.status === 'fulfilled' ? chart7.value : { error: chart7.reason?.message },
      };

    } catch (error) {
      console.error('ObservatoryService.getAllChartsData error:', error);
      throw new Error(`Failed to load charts data: ${error.message}`);
    }
  }


  /**
   * Get summary/aggregate metrics for the selected time period
   * Returns totals and averages: cost, tokens, traces, per-trace metrics
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Summary metrics data
   */
  static async getSummaryMetrics(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filter parameters (same pattern as getChartData)
      if (filters.timePeriod) queryParams.append('time_period', filters.timePeriod);
      if (filters.agentNames && filters.agentNames.length > 0) {
        filters.agentNames.forEach(name => queryParams.append('agent_names', name));
      }
      if (filters.agentTypes && filters.agentTypes.length > 0) {
        filters.agentTypes.forEach(type => queryParams.append('agent_types', type));
      }
      if (filters.executionContexts && filters.executionContexts.length > 0) {
        filters.executionContexts.forEach(ctx => queryParams.append('execution_contexts', ctx));
      }

      const response = await fetch(
        `${this.baseUrl}/api/observatory/summary-metrics?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load summary metrics');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getSummaryMetrics error:', error);
      throw new Error(`Failed to load summary metrics: ${error.message}`);
    }
  }





  /**
   * Get latest feedback across all agents with filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.timePeriod - Time period filter (e.g., "7d", "24h")
   * @param {Array<string>} filters.agentNames - List of agent names to filter by
   * @param {number} limit - Maximum number of feedback items to return
   * @returns {Promise<Object>} Latest feedback data grouped by agent
   */
  static async getLatestFeedback(filters = {}, limit = 20) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit);
      
      console.log('ObservatoryService.getLatestFeedback - Input filters:', filters);
      
      // Add filter parameters
      if (filters.timePeriod) {
        queryParams.append('time_period', filters.timePeriod);
      }
      
      if (filters.agentNames && filters.agentNames.length > 0) {
        console.log('ObservatoryService.getLatestFeedback - Adding agent_names:', filters.agentNames);
        filters.agentNames.forEach(name => queryParams.append('agent_names', name));
      } else {
        console.log('ObservatoryService.getLatestFeedback - No agent names to filter (showing all)');
      }
      
      const url = `${this.baseUrl}/api/observatory/feedback/latest?${queryParams.toString()}`;
      console.log('ObservatoryService.getLatestFeedback - Request URL:', url);

      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load latest feedback');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getLatestFeedback error:', error);
      throw new Error(`Failed to load latest feedback: ${error.message}`);
    }
  }

  /**
   * Get feedback detail for a specific feedback submission
   * @param {string} agentId - Agent ID
   * @param {string} feedbackId - Feedback ID
   * @returns {Promise<Object>} Detailed feedback data
   */
  static async getFeedbackDetail(agentId, feedbackId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/feedback/${agentId}/${feedbackId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load feedback detail');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getFeedbackDetail error:', error);
      throw new Error(`Failed to load feedback detail: ${error.message}`);
    }
  }

  /**
   * Get screenshot for a specific feedback submission
   * @param {string} agentId - Agent ID
   * @param {string} feedbackId - Feedback ID
   * @returns {Promise<string>} Screenshot URL
   */
  static getScreenshotUrl(agentId, feedbackId) {
    return `${this.baseUrl}/api/observatory/feedback/${agentId}/${feedbackId}/screenshot`;
  }

  /**
   * Get root traces (parent_span_id IS NULL) for LLM tracing view
   * @param {Object} filters - Filter parameters
   * @param {number} filters.limit - Maximum number of traces to return (default: 100)
   * @param {number} filters.offset - Offset for pagination (default: 0)
   * @param {string} filters.agentName - Filter by agent name
   * @param {string} filters.agentType - Filter by agent type
   * @param {string} filters.executionContext - Filter by execution context
   * @param {string} filters.status - Filter by status
   * @param {string} filters.startDate - Filter by start date (ISO format)
   * @param {string} filters.endDate - Filter by end date (ISO format)
   * @returns {Promise<Object>} Root traces data
   */
  static async getRootTraces(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filter parameters
      if (filters.limit !== undefined) queryParams.append('limit', filters.limit);
      if (filters.offset !== undefined) queryParams.append('offset', filters.offset);
      if (filters.agentName) queryParams.append('agent_name', filters.agentName);
      if (filters.agentType) queryParams.append('agent_type', filters.agentType);
      if (filters.executionContext) queryParams.append('execution_context', filters.executionContext);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.startDate) queryParams.append('start_date', filters.startDate);
      if (filters.endDate) queryParams.append('end_date', filters.endDate);

      const response = await fetch(
        `${this.baseUrl}/api/observatory/traces/root?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load root traces');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getRootTraces error:', error);
      throw new Error(`Failed to load root traces: ${error.message}`);
    }
  }

  /**
   * Get all spans for a specific trace (for timeline view)
   * @param {string} traceId - Trace ID
   * @returns {Promise<Object>} Trace hierarchy data
   */
  static async getTraceSpans(traceId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/traces/${traceId}/spans`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load trace spans');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getTraceSpans error:', error);
      throw new Error(`Failed to load trace spans: ${error.message}`);
    }
  }

  /**
   * Get detailed information for a specific span
   * @param {string} spanId - Span ID
   * @returns {Promise<Object>} Span detail data
   */
  static async getSpanDetail(spanId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/spans/${spanId}/detail`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load span detail');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getSpanDetail error:', error);
      throw new Error(`Failed to load span detail: ${error.message}`);
    }
  }

  /**
   * Get all alert rules
   * @param {boolean} enabledOnly - Return only enabled rules
   * @returns {Promise<Object>} Alert rules data
   */
  static async getAlertRules(enabledOnly = false) {
    try {
      const queryParams = new URLSearchParams();
      if (enabledOnly) queryParams.append('enabled_only', 'true');

      const response = await fetch(
        `${this.baseUrl}/api/observatory/alert-rules?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load alert rules');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getAlertRules error:', error);
      throw new Error(`Failed to load alert rules: ${error.message}`);
    }
  }

  /**
   * Create a new alert rule
   * @param {Object} rule - Alert rule data
   * @returns {Promise<Object>} Created rule data
   */
  static async createAlertRule(rule) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/alert-rules`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(rule)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create alert rule');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.createAlertRule error:', error);
      throw new Error(`Failed to create alert rule: ${error.message}`);
    }
  }

  /**
   * Update an existing alert rule
   * @param {number} ruleId - Rule ID
   * @param {Object} rule - Updated rule data
   * @returns {Promise<Object>} Updated rule data
   */
  static async updateAlertRule(ruleId, rule) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/alert-rules/${ruleId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(rule)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update alert rule');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.updateAlertRule error:', error);
      throw new Error(`Failed to update alert rule: ${error.message}`);
    }
  }

  /**
   * Delete an alert rule
   * @param {number} ruleId - Rule ID
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteAlertRule(ruleId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/alert-rules/${ruleId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to delete alert rule');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.deleteAlertRule error:', error);
      throw new Error(`Failed to delete alert rule: ${error.message}`);
    }
  }

  /**
   * Get current active alerts
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Current alerts data
   */
  static async getCurrentAlerts(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.severity) queryParams.append('severity', filters.severity);
      if (filters.agentName) queryParams.append('agent_name', filters.agentName);

      const response = await fetch(
        `${this.baseUrl}/api/observatory/alerts/current?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load current alerts');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getCurrentAlerts error:', error);
      throw new Error(`Failed to load current alerts: ${error.message}`);
    }
  }

  /**
   * Clear a specific alert
   * @param {number} alertId - Alert ID
   * @returns {Promise<Object>} Deletion result
   */
  static async clearAlert(alertId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/alerts/current/${alertId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to clear alert');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.clearAlert error:', error);
      throw new Error(`Failed to clear alert: ${error.message}`);
    }
  }

  /**
   * Clear all current alerts
   * @returns {Promise<Object>} Deletion result
   */
  static async clearAllAlerts() {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/observatory/alerts/current`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to clear all alerts');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.clearAllAlerts error:', error);
      throw new Error(`Failed to clear all alerts: ${error.message}`);
    }
  }

  /**
   * Get alert history
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Alert history data
   */
  static async getAlertHistory(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.offset) queryParams.append('offset', filters.offset);
      if (filters.severity) queryParams.append('severity', filters.severity);
      if (filters.agentName) queryParams.append('agent_name', filters.agentName);

      const response = await fetch(
        `${this.baseUrl}/api/observatory/alerts/history?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load alert history');
      }

      return result.data;

    } catch (error) {
      console.error('ObservatoryService.getAlertHistory error:', error);
      throw new Error(`Failed to load alert history: ${error.message}`);
    }
  }
}

export default ObservatoryService;